import React, {PureComponent, Fragment} from "react"
import {injectIntl, InjectIntl, FormattedMessage} from "@irati/utils/lang"
import Loader from "@irati/utils/services/apollo/Loading/Loader"
import groupByDate from "@irati/wallet/helpers/groupWalletItemsByDate"
import getId from "@irati/utils/id"
import TenantWalletBalanceQuery, {TenantWalletBalanceQueryResult} from "@irati/wallet/apollo/query/TenantWallet"
import TenantWalletTransactionQuery, {TenantWalletTransactionsData} from "@irati/wallet/apollo/query/TenantWalletTransactions"
import tracking from "@irati-tracking/wallet/tenantWallet"
import Flex from "@irati/ui/src/Flex"
import {H4} from "@irati/ui/src//Typography/Heading"
import Spacing from "@irati/ui/src/Spacing"

import {StatementItem, Hr, ShowMoreButton} from "../StatementItems"
import DateHeader from "../DateHeader"
import NoData from "../NoData"
import TransactionHeader from "./TransactionHeader"
import {StyledButtonRouterLink} from "./styled"

interface Props extends InjectIntl {
  id: string
}

class TenantWalletTransactions extends PureComponent<Props> {
  hasReceivedData(data: TenantWalletTransactionsData) {
    return data && "statementItems" in data && "results" in data.statementItems && data.statementItems.results.length > 0
  }

  render() {
    const {formatMessage, id} = this.props
    const idNamespace = getId("transactions", this.props)

    return (
      <Spacing marginTop={4}>
        <Flex justifyContent="space-between">
          <H4><FormattedMessage id="wallet.tenant.transactionsHeader" /></H4>
          <TransactionHeader />
        </Flex>
        <TenantWalletBalanceQuery>
          {({data: balanceData}: TenantWalletBalanceQueryResult) => {
            const {ownerUuid, uuid} = balanceData.tenantWallet
            const variables = {
              walletOwnerId: ownerUuid,
              walletId: uuid,
              limit: 15
            }

            return (
              <TenantWalletTransactionQuery variables={variables}>
                {({data: transactionData, fetchMore, loading: isLoading, networkStatus}) => {
                  const hasData = this.hasReceivedData(transactionData)
                  const groupedData = hasData ? groupByDate([], transactionData.statementItems.results) : []
                  const isRefetching = networkStatus === 3
                  const variablesToSend = hasData
                    ? {
                      ...variables,
                      cursor: transactionData.statementItems.cursor
                    }
                    : variables

                  return isLoading && !isRefetching ? (
                    <Loader />
                  ) : (
                    <Fragment>
                      {hasData &&
                        groupedData.results.map(({data, date}) => {
                          const key = date.format("YYYY-MM-DD")
                          return (
                            <Fragment key={key}>
                              <Hr />
                              <DateHeader id={getId(`date-header_${key}`, idNamespace)} date={date} />
                              {data.map((statementItem: StatementItem) => (
                                <StatementItem
                                  id={getId(`statement-item_${statementItem.uuid}`, idNamespace)}
                                  isTenantWallet
                                  statementItem={statementItem}
                                  key={statementItem.uuid} />
                              ))}
                            </Fragment>
                          )
                        })}
                      {isLoading && <Loader />}
                      {hasData && transactionData.statementItems.hasMore && !isLoading && (
                        <ShowMoreButton
                          id={idNamespace}
                          onClick={() => {
                            fetchMore(variablesToSend)
                            tracking.clickTenantShowMore()
                          }}>
                          <FormattedMessage id="wallet.statementItems.rewardWallet.showMoreButton.text" />
                        </ShowMoreButton>
                      )}
                      {!isLoading && !hasData && (
                        <NoData useCardStyle id={id} text={formatMessage({id: "wallet.reward.noScheduledTransfers.text"})}>
                          {balanceData.tenantWallet.availableBalance > 0 ? (
                            <StyledButtonRouterLink onClick={tracking.clickTransferCredit} primary to="/tenant/transfer">
                              <FormattedMessage id="wallet.tenant.topUp.transfer" />
                            </StyledButtonRouterLink>
                          ) : (
                            <StyledButtonRouterLink onClick={tracking.clickTopUp} primary to="/tenant/top-up">
                              <FormattedMessage id="wallet.tenant.topUp.button" />
                            </StyledButtonRouterLink>
                          )}
                        </NoData>
                      )}
                    </Fragment>
                  )
                }}
              </TenantWalletTransactionQuery>
            )
          }}
        </TenantWalletBalanceQuery>
      </Spacing>
    )
  }
}

export default injectIntl(TenantWalletTransactions)
